import React, { Suspense, lazy } from 'react';
import {
    Switch,
    Route, Redirect,
    useLocation,
} from "react-router-dom";
import { getToken, isAdminOnly } from "../services/auth";
import { PAGES } from "./PAGES";
import Login from '../pages/Auth/Login';
import Registration from '../pages/Auth/Registration';
import Layout from "../components/Layout/Layout";
import Loader from 'components/Loader/Loader';
import Skeleton from 'components/Skeleton/Skeleton';
import EventDetailSkeleton from 'pages/EventDetail/EventDetailSkeleton';
import AdminSkeleton from 'pages/Admin/AdminSkeleton';
import { useCommunity, useCurrentUser } from "../context/community";

// Auth section
const Welcome = lazy(() => import('../pages/Welcome/Welcome'));
const Recovery = lazy(() => import('../pages/Auth/Recovery'));
const ConfirmRecovery = lazy(() => import('../pages/Auth/ConfirmRecovery'));
const Confirmed = lazy(() => import('../pages/Confirmed/Confirmed'));
const OneTimeCode = lazy(() => import('../pages/Auth/OneTimeCode/OneTimeCode'));
const AuthMagic = lazy(() => import('../pages/Auth/AuthMagic'));
const Invitation = lazy(() => import('../pages/Auth/Invitation'));
const AccountDeleted = lazy(() => import('../pages/AccountDeleted/AccountDeleted'));

// Admin section
const Event = lazy(() => import('../pages/Event/Event'));
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const ScoreMatch = lazy(() => import('../pages/ScoreMatch/ScoreMatch'));
const AdminNotifications = lazy(() => import('../pages/AdminNotifications/AdminNotifications'));
const AdminSetttings = lazy(() => import('../pages/AdminSettings/AdminSettings'));
const Admin = lazy(() => import('../pages/Admin/Admin'));

// User section
const Questions = lazy(() => import('../pages/Questions/Questions'));
const Onboarding = lazy(() => import('../pages/Onboarding/Onboarding'));
const Profile = lazy(() => import('../pages/Profile/Profile'));
const ContactList = lazy(() => import('../pages/ContactList/ContactList'));
const Participants = lazy(() => import('../pages/Participants/Participants'));
const Mentorship = lazy(() => import('../pages/Mentorship/Mentorship'));
const LastStep = lazy(() => import('../pages/LastStep/LastStep'));
const MyCommunities = lazy(() => import('../pages/MyCommunities/MyCommunities'));
const Subscriptions = lazy(() => import('../pages/Subscriptions/Subscriptions'));
const FullParticipantView = lazy(() => import('../pages/FullParticipantView/FullParticipantView'));
const EventDetail = lazy(() => import('../pages/EventDetail/EventDetail'));
const EventList = lazy(() => import('../pages/EventList/EventList')); // Import EventList
const VacancyList = lazy(() => import('../pages/VacancyList/VacancyList')); // Import VacancyList
const VacancyCreate = lazy(() => import('../pages/VacancyCreate/VacancyCreate')); // Import VacancyCreate
const VacancyDetail = lazy(() => import('../pages/VacancyDetail/VacancyDetail')); // Import VacancyDetail
const VacancyApply = lazy(() => import('../pages/VacancyApply/VacancyApply'));
const VacancyApplications = lazy(() => import('../pages/VacancyApplications/VacancyApplications'));
const VacancyApplicationDetails = lazy(() => import('../pages/VacancyApplicationDetails/VacancyApplicationDetails'));
const VacancyQuestionManager = lazy(() => import('../pages/VacancyQuestionManager/VacancyQuestionManager'));
const EventQuestionManager = lazy(() => import('../pages/EventQuestionManager/EventQuestionManager'));
const EventPhotosManager = lazy(() => import('../pages/EventPhotosManager/EventPhotosManager'));
const EventNotificationsPage = lazy(() => import('../features/event-notifications/EventNotificationsPage'));
const EventLivePage = lazy(() => import('../pages/EventLive/EventLivePage'));
const EventLiveManagePage = lazy(() => import('../pages/EventLive/EventLiveManagePage'));
const EventLivePresentPage = lazy(() => import('../pages/EventLive/EventLivePresentPage'));
const EventLiveDemo = lazy(() => import('../pages/EventLive/EventLiveDemo'));
const Chats = lazy(() => import('../features/chat/pages/ChatPage'));
const EventCreate = lazy(() => import('../pages/EventCreate/EventCreate'));
const FAQ = lazy(() => import('../pages/FAQ/FAQ'));

// VTB section
const VTBServices = lazy(() => import('../pages/VTB/VTBServices'));
const VTBEducation = lazy(() => import('../pages/VTB/VTBEducation'));
const VTBContacts = lazy(() => import('../pages/VTB/VTBContacts'));
const VTBEvents = lazy(() => import('../pages/VTB/VTBEvents'));
const VTBEarnings = lazy(() => import('../pages/VTB/VTBEarnings'));
const VTBCareer = lazy(() => import('../pages/VTB/VTBCareer'));
const NavBarGallery = lazy(() => import('../components/NavBarTemplates/NavBarGallery'));
const KrasavaReference = lazy(() => import('../pages/KrasavaReference/KrasavaReference'));

const MODERATION_STATUS_EVENT_ONLY = 'EVENT_ONLY';

/** Paths EVENT_ONLY users may access: events list, FAQ, profile, profile view /u/:slug, event detail /event/:id (not create), and onboarding flow so they can complete it from the modal. */
const isPathAllowedForEventOnly = (path) => {
    if (path === PAGES.EVENT_LIST || path === PAGES.FAQ || path === PAGES.PROFILE) return true;
    if (path === PAGES.QUESTIONS || path === PAGES.ONBOARDING || path === PAGES.LAST_STEP || path === PAGES.CONFIRMED) return true;
    if (path.startsWith('/u/')) return true;
    if (path.startsWith('/event/') && !path.includes('/event/create')) return true;
    return false;
};

const Page = () => {
    const token = getToken();
    const { pathname } = useLocation();
    const { community, loading } = useCommunity();
    const { moderationStatus } = useCurrentUser();
    const isEventOnly = moderationStatus === MODERATION_STATUS_EVENT_ONLY;
    
    // Check if community has PUBLIC_EVENTS_PAGE option enabled
    const hasPublicEventsPage = React.useMemo(() => {
        if (!community || !community.options) {
            return false;
        }
        const options = Array.isArray(community.options) ? community.options : [];
        return options.includes('PUBLIC_EVENTS_PAGE');
    }, [community]);

    // Check if community has ENABLE_CAREER_TAB option enabled
    const hasCareerTab = React.useMemo(() => {
        if (!community || !community.options) {
            return false;
        }
        const options = Array.isArray(community.options) ? community.options : [];
        const hasOption = options.some(opt => {
            const optionType = typeof opt === 'string' ? opt : (opt?.optionType || opt);
            return optionType === 'ENABLE_CAREER_TAB';
        });
        
        if (process.env.NODE_ENV === 'development') {
            console.log('[Page] Has ENABLE_CAREER_TAB:', hasOption);
        }
        
        return hasOption;
    }, [community]);

    // Check if community has MAKE_CAREER_PUBLIC option enabled (anonymous career view)
    const hasPublicCareerPage = React.useMemo(() => {
        if (!community || !community.options) {
            return false;
        }
        const options = Array.isArray(community.options) ? community.options : [];
        return options.some(opt => {
            const optionType = typeof opt === 'string' ? opt : (opt?.optionType || opt);
            return optionType === 'MAKE_CAREER_PUBLIC';
        });
    }, [community]);

    // Dedicated preview route for reviewing navbar templates without interfering with production Layout
    if (pathname === PAGES.NAVBAR_PREVIEW || pathname === '/navbar-preview') {
        return (
            <Suspense fallback={<Loader />}>
                <NavBarGallery />
            </Suspense>
        );
    }

    // Dedicated reference route for Krasava landing (1-to-1 reference)
    if (pathname === PAGES.KRASAVA_REFERENCE || pathname === '/krasava-reference' || pathname === '/krasava-landing-reference') {
        return (
            <Suspense fallback={<Loader />}>
                <KrasavaReference />
            </Suspense>
        );
    }

    if (!token) {
        // Store the intended destination for redirect after login
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        const currentHash = window.location.hash;
        const fullPath = currentPath + currentSearch + currentHash;
        
        // List of public routes that don't require authentication
        const publicRoutes = [
            PAGES.WELCOME,
            PAGES.LOGIN,
            PAGES.REGISTRATION,
            PAGES.RECOVERY,
            PAGES.CONFIRM_RECOVERY,
            PAGES.ACCOUNT_DELETED,
            PAGES.CONFIRMED,
            PAGES.AUTH_MAGIC,
            PAGES.INVITATION,
            PAGES.ONE_TIME_CODE,
            PAGES.FULL_PARTICIPANT_VIEW,
            PAGES.KRASAVA_REFERENCE,
        ];
        
        // Add events list page to public routes if community option is enabled
        if (hasPublicEventsPage) {
            publicRoutes.push(PAGES.EVENT_LIST);
        }

        // Add career list to public routes if community option is enabled
        if (hasPublicCareerPage) {
            publicRoutes.push(PAGES.VACANCY_LIST);
        }
        
        // Check if current path is a public route (excluding event/career routes which need redirect)
        const isPublicRoute = publicRoutes.includes(currentPath) || currentPath === '/';
        
        // Store redirect path for protected routes and public event/career routes
        // Event/career routes are public but we still want to redirect back to them after login
        const isEventRoute = currentPath.startsWith('/event/') || currentPath === PAGES.EVENT_LIST;
        const isCareerRoute = currentPath === PAGES.VACANCY_LIST
            || currentPath.startsWith('/job/')
            || currentPath.startsWith('/jobs/')
            || currentPath.startsWith('/vacancy/')
            || currentPath.startsWith('/vacancies');
        if (!isPublicRoute || isEventRoute || isCareerRoute) {
            sessionStorage.setItem('redirectToAfterLogin', fullPath);
        }
        
        // Show loader while community is loading (needed to check options)
        if (loading) {
            return (
                <Layout>
                    <Loader />
                </Layout>
            );
        }
        
        return (
            <Layout>
                <Suspense fallback={<Loader />}>
                    <Switch>
                        <Route path={PAGES.WELCOME} exact component={Welcome} />
                        <Route path={PAGES.LOGIN} component={Login} />
                        <Route path={PAGES.REGISTRATION} component={Registration} />
                        <Route path={PAGES.RECOVERY} component={Recovery} />
                        <Route path={PAGES.CONFIRM_RECOVERY} component={ConfirmRecovery} />
                        <Route path={PAGES.ACCOUNT_DELETED} component={AccountDeleted} />
                        <Route path={PAGES.CONFIRMED} component={Confirmed} />
                        <Route path={PAGES.AUTH_MAGIC} component={AuthMagic} />
                        <Route path={PAGES.INVITATION} component={Invitation} />
                        <Route path={PAGES.ONE_TIME_CODE} component={OneTimeCode} />
                        <Route path={PAGES.FULL_PARTICIPANT_VIEW}>
                            <Suspense fallback={<Skeleton page={PAGES.FULL_PARTICIPANT_VIEW} useContainer />}>
                                <FullParticipantView />
                            </Suspense>
                        </Route>
                        {/* Add Event List Route for public access if option is enabled */}
                        {hasPublicEventsPage && (
                            <Route path={PAGES.EVENT_LIST}>
                                <Suspense fallback={<Skeleton useContainer />}>
                                    <EventList />
                                </Suspense>
                            </Route>
                        )}
                        {/* Add Event Detail Route for anonymous users */}
                        <Route path="/live-demo">
                            <Suspense fallback={<Skeleton useContainer />}>
                                <EventLiveDemo />
                            </Suspense>
                        </Route>
                        <Route path="/event/:eventId/live/present">
                            <Suspense fallback={<Skeleton useContainer />}>
                                <EventLivePresentPage />
                            </Suspense>
                        </Route>
                        <Route path="/event/:eventId/live/manage">
                            <Suspense fallback={<Skeleton useContainer />}>
                                <EventLiveManagePage />
                            </Suspense>
                        </Route>
                        <Route exact path="/event/:eventId/live">
                            <Suspense fallback={<Skeleton useContainer />}>
                                <EventLivePage />
                            </Suspense>
                        </Route>
                        <Route path="/event/:eventId/notifications">
                            <Suspense fallback={<Skeleton useContainer />}>
                                <EventNotificationsPage />
                            </Suspense>
                        </Route>
                        <Route path="/event/:eventId">
                            <Suspense fallback={<EventDetailSkeleton />}>
                                <EventDetail />
                            </Suspense>
                        </Route>
                        {/* Career list + detail for anonymous users when MAKE_CAREER_PUBLIC is enabled */}
                        {hasPublicCareerPage && (
                            <Redirect exact from="/vacancies" to={PAGES.VACANCY_LIST} />
                        )}
                        {hasPublicCareerPage && (
                            <Route exact path={PAGES.VACANCY_LIST}>
                                <Suspense fallback={<Skeleton page={PAGES.VACANCY_LIST} />}>
                                    <VacancyList />
                                </Suspense>
                            </Route>
                        )}
                        {hasPublicCareerPage && (
                            <Route
                                exact
                                path="/vacancy/:slug"
                                render={({ match, location }) => (
                                    <Redirect to={{
                                        pathname: `/job/${match.params.slug}`,
                                        search: location.search,
                                        hash: location.hash,
                                    }} />
                                )}
                            />
                        )}
                        {hasPublicCareerPage && (
                            <Route exact path={PAGES.VACANCY_DETAIL}>
                                <Suspense fallback={<Skeleton useContainer />}>
                                    <VacancyDetail />
                                </Suspense>
                            </Route>
                        )}
                        <Redirect to={PAGES.WELCOME} />
                    </Switch>
                </Suspense>
            </Layout>
        )
    }

    // Community admins with USE_PLATFORM keep member pages; only accounts
    // without that permission stay in the legacy admin-only shell.
    if (isAdminOnly()) {
        return (
            <Layout>
                <Suspense fallback={<Loader />}>
                    <Switch>
                        <Redirect exact from={PAGES.MANAGE_CHATS} to={PAGES.ADMIN_CHATS} />
                        <Redirect exact from={PAGES.MANAGE_CHAT_CONVERSATIONS} to={PAGES.ADMIN_CHAT_CONVERSATIONS} />
                        <Redirect exact from={PAGES.MANAGE_CHAT_SETTINGS} to={PAGES.ADMIN_CHAT_SETTINGS_SCENARIOS} />
                        <Redirect from={`${PAGES.MANAGE_CHAT_SETTINGS}/:section`} to={`${PAGES.ADMIN_CHAT_SETTINGS}/:section`} />
                        <Route path={PAGES.ADMIN_CHATS}>
                            <Suspense fallback={<Skeleton useContainer />}>
                                <Admin />
                            </Suspense>
                        </Route>
                        <Route path={PAGES.EVENT} component={Event} />
                        <Route path={PAGES.DASHBOARD} component={Dashboard} />
                        <Route path={PAGES.AUTH_MAGIC} component={AuthMagic} />
                        <Route path={PAGES.NOTIFICATIONS} component={AdminNotifications} />
                        {/* <Route path={PAGES.SETTINGS} component={AdminSetttings} /> */}
                        <Route path={PAGES.SCORE_MATCH} component={ScoreMatch} />
                        <Redirect to={PAGES.EVENT} />
                    </Switch>
                </Suspense>
            </Layout>
        )
    }

    // EVENT_ONLY: creating an event is closed until onboarding is finished. Send them back
    // with a flag so the events page explains that, instead of swallowing the click.
    if (isEventOnly && pathname === PAGES.EVENT_CREATE) {
        return <Redirect to={{ pathname: PAGES.EVENT_LIST, state: { showEventOnlyOnboarding: true } }} />;
    }

    // EVENT_ONLY: direct link or Telegram "community" opens forbidden page → redirect to events
    if (isEventOnly && !isPathAllowedForEventOnly(pathname)) {
        return <Redirect to={PAGES.EVENT_LIST} />;
    }

    return (
        <Layout>
            <Switch>
                <Route path={PAGES.QUESTIONS}>
                    <Suspense fallback={<Skeleton page={PAGES.QUESTIONS} useContainer />}>
                        <Questions />
                    </Suspense>
                </Route>
                <Route path={PAGES.ONBOARDING}>
                    <Suspense fallback={<Skeleton page={PAGES.ONBOARDING} useContainer />}>
                        <Onboarding />
                    </Suspense>
                </Route>
                <Route path={PAGES.LAST_STEP}>
                    <Suspense fallback={<Loader />}>
                        <LastStep />
                    </Suspense>
                </Route>
                <Route path={PAGES.CONFIRMED}>
                    <Suspense fallback={<Loader />}>
                        <Confirmed />
                    </Suspense>
                </Route>
                <Route path={PAGES.AUTH_MAGIC}>
                    <Suspense fallback={<Loader />}>
                        <AuthMagic />
                    </Suspense>
                </Route>
                <Route path={PAGES.CONFIRM_RECOVERY}>
                    <Suspense fallback={<Loader />}>
                        <ConfirmRecovery />
                    </Suspense>
                </Route>
                <Route path={PAGES.INVITATION}>
                    <Suspense fallback={<Loader />}>
                        <Invitation />
                    </Suspense>
                </Route>
                <Route path={PAGES.SUBSCRIPTIONS}>
                    <Suspense fallback={<Loader />}>
                        <Subscriptions />
                    </Suspense>
                </Route>
                <Route path={PAGES.PROFILE}>
                    <Suspense fallback={<Skeleton page={PAGES.PROFILE} useContainer />}>
                        <Profile />
                    </Suspense>
                </Route>
                <Redirect exact from={PAGES.ADMIN_CHAT_SETTINGS} to={PAGES.ADMIN_CHAT_SETTINGS_SCENARIOS} />
                <Route path={PAGES.ADMIN}>
                    <Suspense fallback={<AdminSkeleton />}>
                        <Admin />
                    </Suspense>
                </Route>
                <Route path={PAGES.MY_COMMUNITIES}>
                    <Suspense fallback={<Skeleton page={PAGES.MY_COMMUNITIES} useContainer />}>
                        <MyCommunities />
                    </Suspense>
                </Route>
                <Route path={PAGES.CONTACTS}>
                    <Suspense fallback={<Skeleton useContainer page={PAGES.CONTACTS} />}>
                        <ContactList />
                    </Suspense>
                </Route>
                <Route path={PAGES.PARTICIPANTS}>
                    <Suspense fallback={<Skeleton useContainer page={PAGES.PARTICIPANTS} />}>
                        <Participants />
                    </Suspense>
                </Route>
                <Route path={PAGES.MENTORSHIP}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <Mentorship />
                    </Suspense>
                </Route>
                <Route path={PAGES.FAQ}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <FAQ />
                    </Suspense>
                </Route>
                {/* Add Event List Route */}
                <Route path={PAGES.EVENT_LIST}>
                    <Suspense fallback={<Skeleton useContainer />}> {/* Using Skeleton as fallback */}
                        <EventList />
                    </Suspense>
                </Route>
                {/* Legacy career URLs → /jobs, /job */}
                <Redirect exact from="/vacancies" to={PAGES.VACANCY_LIST} />
                <Redirect exact from="/vacancies/create" to={PAGES.VACANCY_CREATE} />
                <Route
                    path="/vacancies/:slug/applications/:applicationId"
                    render={({ match, location }) => (
                        <Redirect to={{
                            pathname: `/jobs/${match.params.slug}/applications/${match.params.applicationId}`,
                            search: location.search,
                            hash: location.hash,
                        }} />
                    )}
                />
                <Route
                    path="/vacancies/:slug/applications"
                    render={({ match, location }) => (
                        <Redirect to={{
                            pathname: `/jobs/${match.params.slug}/applications`,
                            search: location.search,
                            hash: location.hash,
                        }} />
                    )}
                />
                <Route
                    path="/vacancy/:slug/apply"
                    render={({ match, location }) => (
                        <Redirect to={{
                            pathname: `/job/${match.params.slug}/apply`,
                            search: location.search,
                            hash: location.hash,
                        }} />
                    )}
                />
                <Route
                    path="/vacancy/:slug/questions"
                    render={({ match, location }) => (
                        <Redirect to={{
                            pathname: `/job/${match.params.slug}/questions`,
                            search: location.search,
                            hash: location.hash,
                        }} />
                    )}
                />
                <Route
                    path="/vacancy/:slug"
                    render={({ match, location }) => (
                        <Redirect to={{
                            pathname: `/job/${match.params.slug}`,
                            search: location.search,
                            hash: location.hash,
                        }} />
                    )}
                />
                {/* Career (job) routes */}
                <Route exact path={PAGES.VACANCY_LIST}>
                    <Suspense fallback={<Skeleton page={PAGES.VACANCY_LIST} />}>
                        <VacancyList />
                    </Suspense>
                </Route>
                {/* Add Vacancy Create Route */}
                <Route exact path={PAGES.VACANCY_CREATE}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyCreate />
                    </Suspense>
                </Route>
                <Route exact path={PAGES.VACANCY_APPLY}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyApply />
                    </Suspense>
                </Route>
                <Route exact path={PAGES.VACANCY_APPLICATIONS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyApplications />
                    </Suspense>
                </Route>
                <Route exact path={PAGES.VACANCY_APPLICATION_DETAILS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyApplicationDetails />
                    </Suspense>
                </Route>
                <Route exact path={PAGES.VACANCY_QUESTION_MANAGER}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyQuestionManager />
                    </Suspense>
                </Route>
                <Route path={PAGES.VACANCY_DETAIL}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VacancyDetail />
                    </Suspense>
                </Route>
                <Route path={PAGES.EVENT_CREATE}>
                    <Suspense fallback={<Skeleton page={PAGES.EVENT_CREATE} useContainer />}>
                        <EventCreate />
                    </Suspense>
                </Route>
                {/* Event Question Manager Route - Must come before general event route */}
                <Route path="/event/:eventId/questions">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventQuestionManager />
                    </Suspense>
                </Route>
                {/* Event Photos Manager Route - Must come before general event route */}
                <Route path="/event/:eventId/photos">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventPhotosManager />
                    </Suspense>
                </Route>
                <Route path="/event/:eventId/live/present">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventLivePresentPage />
                    </Suspense>
                </Route>
                <Route path="/event/:eventId/live/manage">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventLiveManagePage />
                    </Suspense>
                </Route>
                <Route exact path="/event/:eventId/live">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventLivePage />
                    </Suspense>
                </Route>
                <Route path="/event/:eventId/notifications">
                    <Suspense fallback={<Skeleton useContainer />}>
                        <EventNotificationsPage />
                    </Suspense>
                </Route>
                {/* Add Event Detail Route */}
                <Route path="/event/:eventId">
                    <Suspense fallback={<EventDetailSkeleton />}>
                        <EventDetail />
                    </Suspense>
                </Route>
                <Route path={PAGES.FULL_PARTICIPANT_VIEW}>
                    <Suspense fallback={<Skeleton page={PAGES.FULL_PARTICIPANT_VIEW} useContainer />}>
                        <FullParticipantView />
                    </Suspense>
                </Route>
                <Redirect exact from={PAGES.MANAGE_CHATS} to={PAGES.ADMIN_CHATS} />
                <Redirect exact from={PAGES.MANAGE_CHAT_CONVERSATIONS} to={PAGES.ADMIN_CHAT_CONVERSATIONS} />
                <Redirect exact from={PAGES.MANAGE_CHAT_SETTINGS} to={PAGES.ADMIN_CHAT_SETTINGS_SCENARIOS} />
                <Redirect from={`${PAGES.MANAGE_CHAT_SETTINGS}/:section`} to={`${PAGES.ADMIN_CHAT_SETTINGS}/:section`} />
                <Route path={PAGES.CHATS}>
                    <Suspense fallback={<Loader />}>
                        <Chats />
                    </Suspense>
                </Route>
                <Route path={PAGES.NOTIFICATIONS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <AdminNotifications />
                    </Suspense>
                </Route>
                {/* VTB Routes */}
                <Route path={PAGES.VTB_SERVICES}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBServices />
                    </Suspense>
                </Route>
                <Route path={PAGES.VTB_EDUCATION}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBEducation />
                    </Suspense>
                </Route>
                <Route path={PAGES.VTB_CONTACTS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBContacts />
                    </Suspense>
                </Route>
                <Route path={PAGES.VTB_EVENTS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBEvents />
                    </Suspense>
                </Route>
                <Route path={PAGES.VTB_EARNINGS}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBEarnings />
                    </Suspense>
                </Route>
                <Route path={PAGES.VTB_CAREER}>
                    <Suspense fallback={<Skeleton useContainer />}>
                        <VTBCareer />
                    </Suspense>
                </Route>
                <Redirect to={isEventOnly ? PAGES.EVENT_LIST : PAGES.MY_COMMUNITIES} />
            </Switch>
        </Layout>

    )
};

export default Page;
